"use client";

import { useEffect, useRef } from "react";

import { readDexSdk } from "@/contexts/dex-sdk";
import { env } from "@/env";
import { boundedKeySet } from "@/hooks/storage/bounded-key-set";
import { getLocalStorageItem, setLocalStorageItem } from "@/hooks/storage/use-local-storage";
import { useAuthState } from "@/hooks/use-auth-state";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { withTimeout } from "@/utils/abort";
import { fetchCCTPCandidateTxHashes } from "@/utils/cctp/discovery";
import {
  formatMintRecipientAddress,
  getChainNameFromDomainId,
  getDomainId,
  isValidHexAttestation,
} from "@/utils/cctp/domains";
import { CCTPTransientError } from "@/utils/cctp/errors";
import { checkMessageClaimed } from "@/utils/cctp/nonce";
import { cctpLogContext, logCctpUsage } from "@/utils/cctp/telemetry";
import { lookupCCTPMessage, validateCCTPTransfer } from "@/utils/cctp/validation";
import { getCCTPTransferFields } from "@/utils/cctp/version";
import { Logger } from "@/utils/logger";
import {
  findEntryByRequestId,
  getProcessedTargetStep,
  sumProcessedByRequestId,
} from "@/utils/withdraw-queue-events";

import { queuedTransferKey } from "./queued-tracker";
import type { TransferProgressData } from "./transfer-progress-card";
import { useTransferProgress } from "./use-transfer-progress";

/** Read `signal.aborted` through a function boundary so TypeScript's CFA
 *  cannot narrow it — the value can change across `await` boundaries. */
function isAborted(signal: AbortSignal): boolean {
  return signal.aborted;
}

// ---------------------------------------------------------------------------
// Cooldown — 5 minutes per wallet+chain
// ---------------------------------------------------------------------------

const LAST_SCANNED_PREFIX = "tp-auto-import-scanned";
const SCAN_COOLDOWN_MS = 5 * 60 * 1000;
const ERROR_COOLDOWN_MS = 60 * 1000; // Shorter cooldown on error to prevent retry storms

function getLastScannedKey(sourceAddress: string, chainName: string): string {
  return `${LAST_SCANNED_PREFIX}-${chainName.toLowerCase()}-${sourceAddress.toLowerCase()}`;
}

function getLastScannedAt(sourceAddress: string, chainName: string): number | null {
  const raw = getLocalStorageItem(getLastScannedKey(sourceAddress, chainName));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as number;
  } catch {
    return null;
  }
}

function isScanCooldownActive(sourceAddress: string, chainName: string): boolean {
  const ts = getLastScannedAt(sourceAddress, chainName);
  if (ts === null) return false;
  return Date.now() - ts < SCAN_COOLDOWN_MS;
}

function setLastScannedNow(sourceAddress: string, chainName: string): void {
  setLocalStorageItem(getLastScannedKey(sourceAddress, chainName), Date.now());
}

/** Set a shorter cooldown on scan error to prevent retry storms. */
function setErrorCooldown(sourceAddress: string, chainName: string): void {
  const errorExpiry = Date.now() - SCAN_COOLDOWN_MS + ERROR_COOLDOWN_MS;
  setLocalStorageItem(getLastScannedKey(sourceAddress, chainName), errorExpiry);
}

// ---------------------------------------------------------------------------
// Redeemed-tx cache — avoids repeated lookups
// ---------------------------------------------------------------------------

/** Per chain + wallet: which tx hashes are already known to be redeemed. */
const redeemedTxHashes = boundedKeySet("tp-cctp-redeemed", 500);

function getCachedRedeemedTxHashes(chainName: string, address: string): Set<string> {
  return redeemedTxHashes(chainName, address).read();
}

// Validate txHash format before writing to cache — prevents malformed API
// responses from permanently skipping legitimate transfers.
// Accepts both hex (EVM/Aptos with 0x prefix) and base58 (Solana) formats.
const HEX_TX_HASH_PATTERN = /^0x[0-9a-f]{1,128}$/i;
const BASE58_TX_HASH_PATTERN = /^[1-9A-HJ-NP-Za-km-z]{32,88}$/;

function isValidTxHash(txHash: string): boolean {
  return HEX_TX_HASH_PATTERN.test(txHash) || BASE58_TX_HASH_PATTERN.test(txHash);
}

function addCachedRedeemedTxHash(chainName: string, address: string, txHash: string): void {
  if (!isValidTxHash(txHash)) {
    Logger.warn("[auto-import] Rejecting invalid txHash for redeemed cache", { chainName, txHash });
    return;
  }
  redeemedTxHashes(chainName, address).add(txHash);
}

// ---------------------------------------------------------------------------
// Per-tx failure tracking — skip persistently failing hashes (24h TTL)
// ---------------------------------------------------------------------------

const FAIL_COUNT_PREFIX = "tp-cctp-fail";
const MAX_TX_FAILURES = 3;
const FAIL_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface FailEntry {
  count: number;
  lastFailedAt: number;
}

function getFailCountKey(chainName: string, address: string): string {
  return `${FAIL_COUNT_PREFIX}-${chainName.toLowerCase()}-${address.toLowerCase()}`;
}

function getFailCounts(chainName: string, address: string): Record<string, FailEntry> {
  const raw = getLocalStorageItem(getFailCountKey(chainName, address));
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, FailEntry>;
    }
  } catch {
    /* ignore corrupt data */
  }
  return {};
}

function recordTxFailure(chainName: string, address: string, txHash: string): boolean {
  const counts = getFailCounts(chainName, address);
  const existing = counts[txHash];
  const now = Date.now();

  // Reset if the last failure was more than 24h ago
  const prevCount = existing && now - existing.lastFailedAt < FAIL_TTL_MS ? existing.count : 0;
  const newCount = prevCount + 1;
  counts[txHash] = { count: newCount, lastFailedAt: now };
  setLocalStorageItem(getFailCountKey(chainName, address), counts);
  return newCount >= MAX_TX_FAILURES;
}

function isTxSkipped(failCounts: Record<string, FailEntry>, txHash: string): boolean {
  const entry = failCounts[txHash];
  if (!entry) return false;
  if (Date.now() - entry.lastFailedAt >= FAIL_TTL_MS) return false;
  return entry.count >= MAX_TX_FAILURES;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Automatically scans for pending CCTP transfers and imports them into
 * the TransferProgress provider. Runs once on mount (subject to 5-min cooldown).
 *
 * Handles both directions:
 * - Deposits (EVM/Solana → Aptos): type "cctp-deposit"
 * - Withdrawals (Aptos → EVM/Solana): type "cctp-withdraw"
 */
const AUTO_IMPORT_ENABLED = env.NEXT_PUBLIC_AUTO_IMPORT_TRANSFERS_ENABLED === true;

export function useAutoImportTransfers(): void {
  const { accountAddress, chainName, ethAddress, solanaAddress, wallet } = useDecibelWallet();
  const { subaccountAddress } = useAuthState();
  const { transfers, addTransfer, updateTransfer } = useTransferProgress();
  const hasScannedRef = useRef(false);
  // Keep a ref to the latest transfers so reconcileStaleQueuedCards can
  // check current state rather than using the stale closure snapshot.
  const transfersRef = useRef(transfers);
  transfersRef.current = transfers;

  const isNotAptos = chainName !== "Aptos";

  // Deposit source: the external EVM/Solana wallet address
  const depositSourceAddress = isNotAptos ? (ethAddress ?? solanaAddress ?? "") : "";
  const depositChainName = isNotAptos ? chainName : "";

  useEffect(() => {
    if (!AUTO_IMPORT_ENABLED) return;
    if (!accountAddress || !subaccountAddress) return;
    if (hasScannedRef.current) return;

    hasScannedRef.current = true;
    const abortController = new AbortController();

    // Build set of sourceTxHashes already in the transfer progress provider
    const existingTxHashes = new Set(
      transfers
        .filter(
          (t): t is TransferProgressData & { type: "cctp-deposit" | "cctp-withdraw" } =>
            t.type === "cctp-deposit" || t.type === "cctp-withdraw",
        )
        .map((t) => t.sourceTxHash),
    );

    // Same key as the WS/poll dedup, so a padding-only spelling difference can't import twice.
    const existingRequestIds = new Set(
      transfers
        .filter(
          (t): t is TransferProgressData & { type: "queued-withdraw" | "queued-xchain-withdraw" } =>
            t.type === "queued-withdraw" || t.type === "queued-xchain-withdraw",
        )
        .map((t) => queuedTransferKey(t.userAddress, t.requestId)),
    );

    // -----------------------------------------------------------------------
    // Deposit scan — EVM/Solana → Aptos (runs when user has an external wallet)
    // -----------------------------------------------------------------------
    const scanDeposits = async (signal: AbortSignal) => {
      if (!depositSourceAddress || !depositChainName) return;
      if (isScanCooldownActive(depositSourceAddress, depositChainName)) return;

      const domainId = getDomainId(depositChainName);
      if (domainId === null) return;

      const lastScannedAt = getLastScannedAt(depositSourceAddress, depositChainName);
      let txHashes: string[];
      try {
        txHashes = await fetchCCTPCandidateTxHashes(
          depositChainName,
          depositSourceAddress,
          lastScannedAt ?? undefined,
        );
      } catch (err) {
        Logger.warn("Failed to discover CCTP deposits", {
          error: err,
          depositChainName,
          depositSourceAddress,
        });
        return;
      }
      if (isAborted(signal)) return;

      if (txHashes.length === 0) {
        setLastScannedNow(depositSourceAddress, depositChainName);
        return;
      }

      const cachedRedeemed = getCachedRedeemedTxHashes(depositChainName, depositSourceAddress);
      const failCounts = getFailCounts(depositChainName, depositSourceAddress);
      let importedCount = 0;
      let hasTransientFailure = false;
      let hasPendingAttestation = false;

      for (const txHash of txHashes) {
        if (isAborted(signal)) break;
        if (existingTxHashes.has(txHash)) continue;
        if (cachedRedeemed.has(txHash)) continue;
        if (isTxSkipped(failCounts, txHash)) continue;

        try {
          const validated = await validateCCTPTransfer(domainId, txHash, accountAddress);

          if (validated === null) continue;
          if (validated === "not_attested") {
            // Keep the cursor so the next scan looks again; it isn't a failure.
            hasPendingAttestation = true;
            continue;
          }
          if (validated === "redeemed") {
            addCachedRedeemedTxHash(depositChainName, depositSourceAddress, txHash);
            continue;
          }

          const { result } = validated;
          const hasAttestation = isValidHexAttestation(result.attestation);
          const currentStep = hasAttestation ? "claimable" : "bridging";

          addTransfer({
            type: "cctp-deposit",
            sourceTxHash: txHash,
            amount: result.amount,
            sourceChain: depositChainName,
            destinationChain: "Aptos",
            destinationAddress: accountAddress,
            subaccountToDepositTo: subaccountAddress,
            currentStep,
            cctpVersion: result.cctpVersion,
            ...(hasAttestation &&
              result.attestation && {
                attestation: result.attestation,
                messageBytes: result.messageBytes,
              }),
          });
          logCctpUsage("auto_import", {
            cctpVersion: result.cctpVersion,
            direction: "deposit",
            chain: depositChainName,
          });

          existingTxHashes.add(txHash);
          importedCount++;
        } catch (err) {
          if (err instanceof CCTPTransientError) {
            // Abort without bumping per-tx counter so a Circle/RPC outage doesn't 24h skip-list legitimate transfers.
            hasTransientFailure = true;
            Logger.warn("Auto-import deposit scan aborted (transient API failure)", {
              error: err,
              depositChainName,
            });
            break;
          }
          const skipped = recordTxFailure(depositChainName, depositSourceAddress, txHash);
          if (!skipped) hasTransientFailure = true;
          Logger.warn(
            "Failed to validate CCTP deposit",
            cctpLogContext(
              { cctpVersion: null, direction: "deposit" },
              { error: err, txHash, depositChainName, skipped },
            ),
          );
        }
      }

      if (!isAborted(signal) && !hasTransientFailure && !hasPendingAttestation) {
        setLastScannedNow(depositSourceAddress, depositChainName);
      }

      if (importedCount > 0) {
        Logger.log("Auto-imported pending CCTP deposits", {
          count: importedCount,
          chainName: depositChainName,
        });
      }
    };

    // -----------------------------------------------------------------------
    // Withdrawal scan — Aptos → EVM/Solana (always runs)
    // -----------------------------------------------------------------------
    const scanWithdrawals = async (signal: AbortSignal) => {
      if (isScanCooldownActive(accountAddress, "Aptos")) return;

      const aptosDomainId = getDomainId("Aptos");
      if (aptosDomainId === null) return;

      const lastScannedAt = getLastScannedAt(accountAddress, "Aptos");
      let txHashes: string[];
      try {
        txHashes = await fetchCCTPCandidateTxHashes(
          "Aptos",
          accountAddress,
          lastScannedAt ?? undefined,
        );
      } catch (err) {
        Logger.warn("Failed to discover CCTP withdrawals", { error: err, accountAddress });
        return;
      }
      if (isAborted(signal)) return;

      if (txHashes.length === 0) {
        setLastScannedNow(accountAddress, "Aptos");
        return;
      }

      const cachedRedeemed = getCachedRedeemedTxHashes("Aptos", accountAddress);
      const failCounts = getFailCounts("Aptos", accountAddress);
      let importedCount = 0;
      let hasTransientFailure = false;
      let hasPendingAttestation = false;

      for (const txHash of txHashes) {
        if (isAborted(signal)) break;
        if (existingTxHashes.has(txHash)) continue;
        if (cachedRedeemed.has(txHash)) continue;
        if (isTxSkipped(failCounts, txHash)) continue;

        try {
          const result = await lookupCCTPMessage(aptosDomainId, txHash);
          if (!result) continue;
          if (result === "not_attested") {
            hasPendingAttestation = true;
            continue;
          }

          const fields = getCCTPTransferFields(result.messageBytes);
          if (!fields) continue;

          const claimStatus = await checkMessageClaimed(result.messageBytes);
          if (claimStatus === "unknown") continue;
          if (claimStatus === "claimed") {
            addCachedRedeemedTxHash("Aptos", accountAddress, txHash);
            continue;
          }

          const destChain = getChainNameFromDomainId(fields.destDomain);
          if (!destChain) continue;

          // For Solana, the CCTP mintRecipient is the USDC ATA (token account),
          // not the wallet pubkey. Use the connected wallet address instead.
          // For EVM, the mintRecipient is the wallet address (left-padded to 32 bytes).
          const destAddress =
            destChain === "Solana"
              ? solanaAddress
              : formatMintRecipientAddress(fields.mintRecipient, fields.destDomain);
          if (!destAddress) continue;

          // Verify the destination matches the connected wallet
          const expectedDestAddress = destChain === "Solana" ? solanaAddress : ethAddress;
          // oxlint-disable-next-line custom/no-raw-address-comparison -- may be Solana base58, which is case-sensitive; canonicalizing would collide distinct addresses
          if (destAddress.toLowerCase() !== expectedDestAddress?.toLowerCase()) continue;

          const hasAttestation = isValidHexAttestation(result.attestation);
          const currentStep = hasAttestation ? "claimable" : "bridging";

          addTransfer({
            type: "cctp-withdraw",
            sourceTxHash: txHash,
            amount: result.amount,
            sourceChain: "Aptos",
            destinationChain: destChain,
            destinationAddress: destAddress,
            accountToWithdrawTo: destAddress,
            // No per-transfer subaccount in the CCTP burn scan; use the connected
            // subaccount (guarded non-null at the top of the effect).
            subaccountAddress,
            currentStep,
            cctpVersion: result.cctpVersion,
            ...(hasAttestation &&
              result.attestation && {
                attestation: result.attestation,
                messageBytes: result.messageBytes,
              }),
          });
          logCctpUsage("auto_import", {
            cctpVersion: result.cctpVersion,
            direction: "withdraw",
            chain: destChain,
          });

          existingTxHashes.add(txHash);
          importedCount++;
        } catch (err) {
          if (err instanceof CCTPTransientError) {
            hasTransientFailure = true;
            Logger.warn("Auto-import withdrawal scan aborted (transient API failure)", {
              error: err,
              sourceChain: "Aptos",
            });
            break;
          }
          const skipped = recordTxFailure("Aptos", accountAddress, txHash);
          if (!skipped) hasTransientFailure = true;
          Logger.warn(
            "Failed to validate CCTP withdrawal",
            cctpLogContext(
              { cctpVersion: null, direction: "withdraw" },
              { error: err, txHash, sourceChain: "Aptos" },
            ),
          );
        }
      }

      if (!isAborted(signal) && !hasTransientFailure && !hasPendingAttestation) {
        setLastScannedNow(accountAddress, "Aptos");
      }

      if (importedCount > 0) {
        Logger.log("Auto-imported pending CCTP withdrawals", { count: importedCount });
      }
    };

    // -----------------------------------------------------------------------
    // Queued withdrawal scan — auto-import pending on-chain queued withdrawals
    // so that device-switch or localStorage-clear doesn't lose progress cards
    // -----------------------------------------------------------------------
    const scanQueuedWithdrawals = async (signal: AbortSignal): Promise<string[]> => {
      if (!accountAddress) return [];
      if (isScanCooldownActive(accountAddress, "withdraw-queue")) return [];

      try {
        const response = await readDexSdk.withdrawQueue.getByAddr({
          subAddr: accountAddress,
          status: "Queued",
          limit: 50,
          fetchOptions: { signal },
        });
        if (isAborted(signal)) return [];

        let importedCount = 0;

        for (const entry of response.items) {
          const compositeKey = queuedTransferKey(accountAddress, entry.request_id);
          if (existingRequestIds.has(compositeKey)) continue;

          // Cross-chain queued withdrawals are imported with a degraded card.
          // The API doesn't include destinationChain, so the bridge leg can't
          // auto-resume, but at least the user sees pending funds exist.
          const isXchain = entry.recipient != null && entry.recipient !== entry.user;

          if (isXchain) {
            addTransfer({
              type: "queued-xchain-withdraw",
              amount: entry.fungible_amount,
              sourceChain: "Aptos",
              destinationAddress: entry.recipient ?? accountAddress,
              destinationChain: "Aptos",
              currentStep: "withdrawal-queued",
              requestId: entry.request_id,
              userAddress: accountAddress.toLowerCase(),
              accountToWithdrawTo: entry.recipient ?? accountAddress,
              // `entry.user` is the withdrawing subaccount from the on-chain queue record.
              subaccountAddress: entry.user,
              withdrawAmount: entry.fungible_amount,
            });
          } else {
            addTransfer({
              type: "queued-withdraw",
              amount: entry.fungible_amount,
              sourceChain: "Aptos",
              destinationAddress: entry.recipient ?? accountAddress,
              destinationChain: "Aptos",
              currentStep: "withdrawal-queued",
              requestId: entry.request_id,
              userAddress: accountAddress.toLowerCase(),
              // `entry.user` is the withdrawing subaccount from the on-chain queue record.
              subaccountAddress: entry.user,
            });
          }

          existingRequestIds.add(compositeKey);
          importedCount++;
        }

        if (!isAborted(signal)) {
          setLastScannedNow(accountAddress, "withdraw-queue");
        }

        if (response.items.length === 50) {
          Logger.warn("[withdraw-queue] Auto-import hit limit of 50 — some entries may be missed");
        }

        if (importedCount > 0) {
          Logger.log("Auto-imported pending queued withdrawals", { count: importedCount });
        }

        return response.items.map((e) => e.request_id);
      } catch (error) {
        if (!isAborted(signal)) {
          Logger.warn("Failed to scan for queued withdrawals", { error });
          // Prevent retry storms on repeated API errors
          if (accountAddress) setErrorCooldown(accountAddress, "withdraw-queue");
        }
        return [];
      }
    };

    // -----------------------------------------------------------------------
    // Stale card reconciliation — resolve queued cards that were processed or
    // cancelled while the user was offline (prevents stuck undismissible cards)
    // -----------------------------------------------------------------------
    const reconcileStaleQueuedCards = async (
      signal: AbortSignal,
      liveQueuedRequestIds: string[],
    ) => {
      if (!accountAddress) return;

      // Read from transfersRef for latest state instead of the stale closure,
      // so cards already advanced by WS are skipped rather than overwritten.
      const staleCards = transfersRef.current.filter(
        (t): t is TransferProgressData & { type: "queued-withdraw" | "queued-xchain-withdraw" } =>
          (t.type === "queued-withdraw" || t.type === "queued-xchain-withdraw") &&
          t.currentStep === "withdrawal-queued",
      );
      if (staleCards.length === 0) return;

      // Use the live queued IDs from scanQueuedWithdrawals to avoid a duplicate API call
      const liveQueuedIds = new Set(liveQueuedRequestIds);

      for (const card of staleCards) {
        if (isAborted(signal)) break;
        if (liveQueuedIds.has(card.requestId)) continue; // Still queued on-chain

        // Re-check latest state before each card — WS may have advanced it
        // during the async API calls above.
        const latestCard = transfersRef.current.find((t) => t.id === card.id);
        if (latestCard?.currentStep !== "withdrawal-queued") continue;

        // Card's requestId is no longer in the queue — check terminal status.
        // Uses sumProcessedByRequestId to accumulate partial fills across
        // multiple Processed rows, mirroring the poll path logic.
        try {
          const processedResult = await withTimeout(signal, (s) =>
            sumProcessedByRequestId(card.userAddress, card.requestId, s, card.amount),
          );

          if (processedResult.found) {
            const latestAfterProcessed = transfersRef.current.find((t) => t.id === card.id);
            if (latestAfterProcessed?.currentStep !== "withdrawal-queued") continue;

            if (processedResult.total < card.amount - 1e-6) {
              updateTransfer(card.id, { processedAmount: processedResult.total });
              // Fall through to Cancelled check — remainder may have been cancelled.
            } else {
              updateTransfer(card.id, { currentStep: getProcessedTargetStep(card) });
              continue;
            }
          }

          const cancelledMatch = await withTimeout(signal, (s) =>
            findEntryByRequestId(card.userAddress, "Cancelled", card.requestId, s),
          );
          if (cancelledMatch) {
            const latestAfterCancelled = transfersRef.current.find((t) => t.id === card.id);
            if (latestAfterCancelled?.currentStep !== "withdrawal-queued") continue;

            updateTransfer(card.id, {
              currentStep: "withdrawal-cancelled",
              cancelReason: cancelledMatch.cancel_reason ?? undefined,
            });
            continue;
          }

          // Neither found — indexer lag, poll will handle
        } catch {
          // Skip this card, poll will handle
        }
      }
    };

    // Run all scans in parallel — allSettled so one scan failing doesn't
    // prevent the others from completing. scanQueuedWithdrawals feeds its
    // live request IDs into reconcileStaleQueuedCards to avoid a duplicate call.
    const runScans = async () => {
      const queuedScanPromise = scanQueuedWithdrawals(abortController.signal);
      const results = await Promise.allSettled([
        scanDeposits(abortController.signal),
        scanWithdrawals(abortController.signal),
        queuedScanPromise.then((liveIds) =>
          reconcileStaleQueuedCards(abortController.signal, liveIds),
        ),
      ]);
      for (const result of results) {
        if (result.status === "rejected") {
          Logger.warn("Failed to scan for pending transfers", { error: result.reason });
        }
      }
    };

    void runScans();

    return () => {
      abortController.abort();
      // Reset so the next effect run (Strict Mode remount, dep change) can retry
      hasScannedRef.current = false;
    };
    // Only run on address/chain/wallet changes, not on transfers changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    depositSourceAddress,
    accountAddress,
    subaccountAddress,
    depositChainName,
    solanaAddress,
    wallet,
  ]);
}
